/**
 * HTTP Response Interceptors
 *
 * Interceptors that run after each response is received.
 */

import type { AxiosError, AxiosResponse } from "axios";

import { PUBLIC_AUTH_ENDPOINTS } from "@/config/api";
import { clearSessionStorage, dispatchForcedLogout, redirectToLogin } from "@/features/auth/state/session";
import { httpLogger } from "@/lib/logger";
import { getRequestContext } from "../requestContext";

const isPublicAuthEndpoint = (url?: string): boolean => {
    if (!url) {
        return false;
    }

    const normalizedUrl = url.split("?")[0];

    return PUBLIC_AUTH_ENDPOINTS.some((endpoint) => (
        normalizedUrl === endpoint || normalizedUrl.endsWith(endpoint)
    ));
};

/**
 * Handles successful responses
 * Logs response info in development
 */
export const successInterceptor = (response: AxiosResponse): AxiosResponse => {
    const { status, config } = response;
    const method = config.method?.toUpperCase() ?? "GET";
    const { baseURL, path, resolvedUrl } = getRequestContext(config);

    httpLogger.debug(`${method} ${resolvedUrl} → ${status}`, { baseURL, path });

    return response;
};

/**
 * Handles error responses
 * Manages token expiration and redirects
 */
export const errorInterceptor = (error: AxiosError): Promise<never> => {
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase() ?? "GET";
    const { baseURL, path, resolvedUrl } = getRequestContext(error.config ?? {});
    const code = error.code;
    const timeout = error.config?.timeout;

    // Direct console.error for debugging (always visible)
    console.error(`[HTTP] ← ${method} ${resolvedUrl} failed`, {
        baseURL,
        path,
        status: status ?? "no-response",
        code,
        timeout,
        message: error.message,
    });

    // Handle 401/403 by ending the client session and forcing re-authentication
    if (status === 401 || status === 403) {
        if (isPublicAuthEndpoint(path)) {
            httpLogger.warn(`${method} ${resolvedUrl} → ${status}. Authentication request rejected.`, {
                baseURL,
                path,
            });
            return Promise.reject(error);
        }

        const reason = status === 401 ? "unauthorized" : "forbidden";
        httpLogger.warn(`${method} ${resolvedUrl} → ${status}. Forcing re-authentication.`, {
            baseURL,
            path,
        });
        clearSessionStorage();
        dispatchForcedLogout(reason);
        redirectToLogin();
    }

    // Handle 500+ Server errors
    else if (status && status >= 500) {
        httpLogger.error(`${method} ${resolvedUrl} → ${status} Server Error`, {
            baseURL,
            path,
            message: error.message,
        });
    }

    // Handle request timeouts
    else if (code === "ECONNABORTED") {
        httpLogger.error(`${method} ${resolvedUrl} → Timeout`, {
            baseURL,
            path,
            message: error.message,
            timeout,
        });
    }

    // Handle network errors
    else if (!error.response) {
        httpLogger.error(`${method} ${resolvedUrl} → Network Error`, {
            baseURL,
            path,
            code,
            message: error.message,
        });
    }

    // Handle other HTTP errors (4xx)
    else {
        httpLogger.warn(`${method} ${resolvedUrl} → ${status}`, {
            baseURL,
            path,
            message: error.message,
        });
    }

    return Promise.reject(error);
};
